import { captureReactException } from '@sentry/react'
import { AlertTriangle } from 'lucide-react'
import type { ReactNode } from 'react'
import { Component } from 'react'
import { ErrorPage } from './ErrorPage'
import { Button } from './ui/button'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    captureReactException(error, errorInfo)
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <ErrorPage
          icon={AlertTriangle}
          title="Something went wrong"
          description={
            this.state.error?.message ||
            'An unexpected error occurred. Please try refreshing the page.'
          }
          action={
            <>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Reload Page
              </button>
              <Button
                variant="outline"
                onClick={() => {
                  window.location.href = '/'
                }}
              >
                {' '}
                Return to Home{' '}
              </Button>
            </>
          }
        />
      )
    }

    return this.props.children
  }
}
